import React, { useState, useEffect } from "react";

const SubscriptionManagement = () => {
    const [subscription, setSubscription] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [showResumeModal, setShowResumeModal] = useState(false);

    useEffect(() => {
        fetchSubscriptionDetails();
    }, []);

    const fetchSubscriptionDetails = async () => {
        try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/subscription`, {
            method: "GET",
            headers: {
            "Authorization": `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json"
            },
        });

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        setSubscription(data);
        } catch (err) {
        setError(`Error fetching subscription details: ${err.message}`);
        } finally {
        setLoading(false);
        }
    };

    const handleCancelSubscription = async () => {
        try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/subscription/cancel`, {
            method: "POST",
            headers: {
            "Authorization": `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json"
            },
        });

        if (!response.ok) {
            throw new Error("Failed to cancel subscription.");
        }

        alert("Your subscription has been set to cancel at the end of the billing period.");
        setShowCancelModal(false); // Close modal
        fetchSubscriptionDetails(); // Refresh UI
        } catch (err) {
        setError("Failed to cancel subscription.");
        }
    };

    const handleResumeSubscription = async () => {
        try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/subscription/resume`, {
            method: "POST",
            headers: {
            "Authorization": `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json"
            },
        });

        if (!response.ok) {
            throw new Error("Failed to resume subscription.");
        }

        alert("Your subscription has been resumed.");
        setShowResumeModal(false); // Close modal
        fetchSubscriptionDetails(); // Refresh UI
        } catch (err) {
        setError("Failed to resume subscription.");
        }
    };

    const handleChangePaymentMethod = async () => {
        try {
          const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/subscription/billing-portal`, {
            method: "POST",
            headers: {
              "Authorization": `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json"
            },
          });
      
          if (!response.ok) {
            throw new Error("Failed to retrieve billing portal link.");
          }
      
          const data = await response.json();
          window.location.href = data.url; // ✅ Redirect user to Stripe billing portal
        } catch (err) {
          alert("Failed to open billing portal.");
        }
    };
      

    if (loading) return (
        <div className="flex flex-col items-center justify-center h-[300px] text-center">
          <div className="w-10 h-10 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-sm text-gray-600">Loading subscription details...</p>
        </div>
      );
      
    if (error) return <p style={{ color: "red" }}>{error}</p>;
    if (!subscription) return <p>No subscription data found.</p>;

    // ✅ Check if user is on the Free plan
    const isFreePlan = subscription.planType === "Free";

    // Format Subscription Status
    const formattedStatus = subscription.status === "trialing" 
    ? "Free Trial"
    : subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1); // Capitalize first letter

    // ✅ Handle Null Dates (Prevents "12/31/1969" issue)
    const formatDate = (date) => date ? new Date(date).toLocaleDateString() : "N/A";

    return (
        <div className="max-w-3xl mx-auto px-4 py-8 bg-white rounded shadow-sm">
          <h2 className="text-2xl font-bold text-purple-800 mb-4">Manage Subscription</h2>
      
          {error && <p className="text-red-600 mb-4">{error}</p>}
      
          {/* Subscription Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="mb-8">
                <h3 className="text-lg font-semibold mb-2">Subscription Information</h3>
                <p><strong>Plan:</strong> {subscription.planType.charAt(0).toUpperCase() + subscription.planType.slice(1)}</p>
                <p><strong>Status:</strong> {formattedStatus}</p>
        
                {subscription.status === "trialing" && (
                <p className="text-blue-600 mt-2">
                    Free Trial - Ends on <strong>{new Date(subscription.trialEnd).toLocaleDateString()}</strong>
                </p>
                )}
        
                <p><strong>Start Date:</strong> {formatDate(subscription.startDate)}</p>
                <p><strong>Current Period Start:</strong> {formatDate(subscription.currentPeriodStart)}</p>
        
                {!isFreePlan && (
                subscription.cancelAtPeriodEnd ? (
                    <>
                    <p className="text-orange-600 mt-4">
                        Your subscription is set to cancel on <strong>{formatDate(subscription.pendingCancellationDate)}</strong>.
                    </p>
                    <button
                        onClick={() => setShowResumeModal(true)}
                        className="mt-3 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                    >
                        Resume Subscription
                    </button>
                    </>
                ) : (
                    <button
                    onClick={() => setShowCancelModal(true)}
                    className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                    >
                    Cancel Subscription
                    </button>
                )
                )}
            </div>
        
            {/* Billing Details */}
            <div>
                <h3 className="text-lg font-semibold mb-2">Billing Information</h3>
                <p><strong>Next Billing Date:</strong> {formatDate(subscription.nextBillingDate)}</p>
                <p><strong>Amount Due:</strong> ${typeof subscription.amountDue === "number" ? subscription.amountDue.toFixed(2) : "0.00"}</p>
        
                {!isFreePlan && (
                <>
                    <p className="mt-2 text-sm text-gray-600">
                    Manage billing, switch plans, or view billing history below:
                    </p>
                    <button
                    onClick={handleChangePaymentMethod}
                    className="mt-3 px-4 py-2 bg-purple-700 text-white rounded hover:bg-purple-800"
                    >
                    Manage Payments
                    </button>
                </>
                )}
            </div>
          </div>
      
          {/* Cancel Modal */}
          {showCancelModal && (
            <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
              <div className="bg-white p-6 rounded shadow-lg max-w-md w-full">
                <h3 className="text-lg font-semibold mb-2">Cancel Subscription</h3>
                <p className="mb-2">Are you sure you want to cancel?</p>
                <p>Your subscription stays active until <strong>{formatDate(subscription.nextBillingDate)}</strong>.</p>
                <div className="mt-4 flex justify-end gap-3">
                  <button
                    onClick={handleCancelSubscription}
                    className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                  >
                    Confirm Cancel
                  </button>
                  <button
                    onClick={() => setShowCancelModal(false)}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                  >
                    Don't Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
      
          {/* Resume Modal */}
          {showResumeModal && (
            <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
              <div className="bg-white p-6 rounded shadow-lg max-w-md w-full">
                <h3 className="text-lg font-semibold mb-2">Resume Subscription</h3>
                <p className="mb-2">Confirm resuming your subscription.</p>
                <p>Next billing date: <strong>{formatDate(subscription.nextBillingDate)}</strong></p>
                <p>Amount due: <strong>${subscription.amountDue?.toFixed(2) || "0.00"}</strong></p>
                <div className="mt-4 flex justify-end gap-3">
                  <button
                    onClick={handleResumeSubscription}
                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                  >
                    Yes, Resume
                  </button>
                  <button
                    onClick={() => setShowResumeModal(false)}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
    );
};

export default SubscriptionManagement;
